import type { FieldType } from '@blooq/flow'
import { IonButton, IonIcon, IonInput, IonTextarea } from '@ionic/react'
import { arrowUpOutline, micOutline, stop } from 'ionicons/icons'
import { useEffect, useRef } from 'react'

/*
 * The one text box in the app.
 *
 * A pill, a round button, and the platform's own keyboard — every free-text
 * answer goes through this, whether it is a name, a mobile number or an FAQ
 * question. One composer rather than one per dock so the thing a customer
 * types into is in the same place with the same shape on every step, which is
 * what makes a sequence of questions read as a conversation.
 *
 * Two forms of the same thing. A field is one line, because a name is one
 * line. The front door's box is a sentence, so it grows, and it can be given a
 * microphone: empty, the round button listens; with words in it, it sends;
 * while listening, it stops. The button is always the next thing to do, which
 * is what lets it be the only button.
 */

export interface ComposerProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder: string
  /** Decides the keyboard. `text` when the caller has no better idea. */
  kind?: FieldType | 'question'
  /** Filled in for the browser's autofill; `off` otherwise. */
  autocomplete?: 'name' | 'tel' | 'email' | 'off'
  maxlength?: number
  disabled?: boolean
  /** False while the answer is not yet acceptable. The button greys out. */
  ready: boolean
  /** Focus on mount — set when the composer appears in answer to a new question. */
  autofocus?: boolean
  /** A sentence rather than a field: the box grows with it, Enter sends. */
  multiline?: boolean
  /** Shown but not editable — the running transcript while the mic is open. */
  readonly?: boolean
  /** Offer a microphone in place of Send while the box is empty. */
  mic?: { listening: boolean; onToggle: () => void }
}

export function Composer({
  value,
  onChange,
  onSubmit,
  placeholder,
  kind = 'text',
  autocomplete = 'off',
  maxlength,
  disabled,
  ready,
  autofocus,
  multiline,
  readonly,
  mic,
}: ComposerProps) {
  const input = useRef<HTMLIonInputElement>(null)
  const area = useRef<HTMLIonTextareaElement>(null)

  /*
   * Focus a beat after mount, not on it.
   *
   * The dock animates in over 300ms and Ionic's input needs to exist before
   * setFocus can find it; focusing synchronously lands on the previous frame's
   * element or nothing. Only on request — a composer under a list of choices
   * must not summon the keyboard over them.
   */
  useEffect(() => {
    if (!autofocus) return
    const timer = window.setTimeout(
      () => void (multiline ? area.current : input.current)?.setFocus(),
      120,
    )
    return () => window.clearTimeout(timer)
  }, [autofocus, multiline])

  const submit = () => {
    if (ready && !disabled && !readonly) onSubmit()
  }

  /*
   * Which button.
   *
   * Listening wins: the customer must always be able to stop. Otherwise the
   * microphone is offered only while there is nothing to send — the moment a
   * word is typed the button becomes Send, and a customer who wants to dictate
   * clears the box, which is the same rule every messaging app taught them.
   */
  const button = mic?.listening ? 'stop' : mic && value.trim() === '' ? 'mic' : 'send'

  return (
    <form
      className="blooq-composer"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      {multiline ? (
        <IonTextarea
          ref={area}
          className="blooq-composer__field blooq-composer__field--grow"
          aria-label={placeholder}
          placeholder={placeholder}
          autoGrow
          rows={1}
          autocapitalize="sentences"
          enterkeyhint="send"
          maxlength={maxlength}
          value={value}
          disabled={disabled}
          readonly={readonly}
          onIonInput={(event) => onChange(String(event.detail.value ?? ''))}
          /* Enter sends, as the keyboard's own Send key promises. Shift+Enter
             keeps a line break for the desktop customer who wants one. */
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              submit()
            }
          }}
        />
      ) : (
        <IonInput
          ref={input}
          className="blooq-composer__field"
          aria-label={placeholder}
          placeholder={placeholder}
          /* `email` and `tel` bring up the right keyboard; everything else is
             text, because `number` on a phone is a spinner nobody wants. */
          type={kind === 'email' ? 'email' : kind === 'mobile' ? 'tel' : 'text'}
          inputmode={
            kind === 'age' ? 'numeric' : kind === 'mobile' ? 'tel' : kind === 'email' ? 'email' : 'text'
          }
          autocomplete={autocomplete}
          autocapitalize={kind === 'text' || kind === 'question' ? 'sentences' : 'off'}
          enterkeyhint="send"
          maxlength={maxlength}
          value={value}
          disabled={disabled}
          readonly={readonly}
          onIonInput={(event) => onChange(String(event.detail.value ?? ''))}
        />
      )}

      {button === 'send' ? (
        <IonButton
          className="blooq-composer__send"
          type="submit"
          disabled={!ready || disabled}
          aria-label="Send"
        >
          <IonIcon slot="icon-only" icon={arrowUpOutline} />
        </IonButton>
      ) : (
        <IonButton
          className={`blooq-composer__send blooq-composer__${button}`}
          type="button"
          disabled={disabled}
          aria-label={button === 'stop' ? 'Stop listening' : 'Speak instead'}
          aria-pressed={button === 'stop'}
          onClick={mic?.onToggle}
        >
          <IonIcon slot="icon-only" icon={button === 'stop' ? stop : micOutline} />
        </IonButton>
      )}
    </form>
  )
}
